import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import {
  AnswerInput,
  ProgramQuestionWithAnswer,
  useProgram,
} from 'shared/providers/ProgramProvider';
import {
  getQuestionOptions,
  secondaryServiceSubTypeQuestionId,
  serviceAmountQuestionId,
  serviceDeliveryQuestionId,
  serviceDurationQuestionId,
  serviceFrequencyQuestionId,
  serviceSubTypeQuestionId,
} from 'npo/pages/ReportBuilder/Components/Activities/options';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import SelectorField from './SelectorField';
import { useBlockedSignal } from './useBlockedSignal';

const ACTIVITY_QUESTION_IDS: ReadonlySet<number> = new Set([
  serviceDeliveryQuestionId,
  serviceSubTypeQuestionId,
  secondaryServiceSubTypeQuestionId,
  serviceAmountQuestionId,
  serviceFrequencyQuestionId,
  serviceDurationQuestionId,
]);

export const isActivityQuestion = (
  question: Pick<ProgramQuestionWithAnswer, 'questionId'>,
): boolean => ACTIVITY_QUESTION_IDS.has(question.questionId);

export const HELPER_TEXT_AS_TITLE_QUESTION_IDS = [
  serviceAmountQuestionId,
  serviceFrequencyQuestionId,
  serviceDurationQuestionId,
];

interface Props {
  question: ProgramQuestionWithAnswer;
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
  onBlockedChange?: (blocked: boolean) => void;
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.tiny};
  margin-bottom: ${SPACERS.small};

  .enum-option {
    display: flex;
    gap: ${SPACERS.tiny};
    align-items: flex-start;
    padding: ${SPACERS.tiny};
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    &:hover {
      background-color: ${COLORS.grayLight};
    }
    input[type='radio'] {
      accent-color: ${COLORS.ctaPrimary};
      margin-top: 3px;
    }
  }

  .helper {
    margin-bottom: ${SPACERS.tiny};
  }

  .primary-note {
    margin-bottom: ${SPACERS.tiny};
  }
`;

const useAnswerText = (questionId: number): string | undefined => {
  const { findQuestionById } = useProgram();
  const found = findQuestionById(questionId);
  const answer = found?.question.answer;
  if (!answer) return undefined;
  if (answer.text && answer.text.trim().length > 0) return answer.text;
  if (answer.questionText && answer.questionText.trim().length > 0) {
    return answer.questionText;
  }
  return undefined;
};

const ActivityQuestionRenderer: React.FC<Props> = ({
  question,
  value,
  onChange,
  disabled,
  onBlockedChange,
}) => {
  const [local, setLocal] = useState<AnswerInput>(value);

  useEffect(() => {
    setLocal(value);
  }, [value, question.questionId]);

  const serviceDelivery = useAnswerText(serviceDeliveryQuestionId);
  const serviceSubType = useAnswerText(serviceSubTypeQuestionId);

  const questionOptions = useMemo(
    () =>
      getQuestionOptions(question.questionId, serviceDelivery, serviceSubType),
    [question.questionId, serviceDelivery, serviceSubType],
  );

  const update = (patch: Partial<AnswerInput>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange(next);
  };

  useBlockedSignal(!questionOptions, onBlockedChange);

  const selected = local.text ?? null;

  // Clear a stale selection whenever the current value is no longer a
  // valid option (e.g. serviceDelivery changed upstream).
  useEffect(() => {
    if (!questionOptions) return;
    if (selected && !questionOptions.options.includes(selected)) {
      update({ text: null, questionText: null });
    }
  }, [questionOptions, selected]);

  if (!questionOptions) {
    return (
      <TrueImpactText variant="body-small" color="critical">
        Please answer the previous Activities questions first.
      </TrueImpactText>
    );
  }

  return (
    <Field>
      {questionOptions.helperText &&
        !HELPER_TEXT_AS_TITLE_QUESTION_IDS.includes(question.questionId) && (
          <TrueImpactText variant="body-small" className="helper">
            {questionOptions.helperText}
          </TrueImpactText>
        )}
      {question.questionId === secondaryServiceSubTypeQuestionId &&
        serviceSubType && (
          <TrueImpactText variant="body-small" className="primary-note">
            <strong>Primary:</strong> {serviceSubType}
          </TrueImpactText>
        )}
      <SelectorField>
        <SelectorOptions
          options={questionOptions.options}
          variant="radio"
          margin="0"
          maxWidth="100%"
          disabled={disabled}
          getLabel={opt =>
            opt.startsWith('N/A') ? <strong>{opt}</strong> : opt
          }
          getSelected={opt => selected === opt}
          onClick={opt => {
            if (disabled) return;
            update({ text: opt, questionText: opt });
          }}
        />
      </SelectorField>
    </Field>
  );
};

export default ActivityQuestionRenderer;
